import React, { Fragment, FC, useState } from 'react';
import { Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { MutationFunction } from 'react-apollo';
import { without } from 'lodash';

import { useClient, useNotify } from 'hooks';
import { deleteContentDomain } from 'apollo/reducers';
import { AdminUpdateClientComponent, GetAllClientUrlParamsDocument } from 'api';
import { adminUpdateClientResponse } from 'apollo/responses';
import { Alert, CustomSVG } from 'components';

interface IProps {
  contentDomain: string;
}

const DeleteContentDomain: FC<IProps> = ({ contentDomain }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const notify = useNotify();
  const { id, content_domains } = useClient();

  function handleNotification() {
    notify.info({ message: t('admin:deleteContentDomainSuccess') });
  }

  function handleDelete(mutation: MutationFunction) {
    const values = {
      id,
      content_domains: without(content_domains, contentDomain),
    };

    setOpen(false);

    mutation({
      variables: {
        data: {
          id,
          content_domains: without(content_domains, contentDomain),
        },
      },
      optimisticResponse: adminUpdateClientResponse(values),
      update: cache => {
        deleteContentDomain({
          cache,
          query: GetAllClientUrlParamsDocument,
          variables: {},
          contentDomain,
        });
      },
    });
  }

  function renderConfirmation() {
    return (
      <AdminUpdateClientComponent onError={notify.mutationError} onCompleted={handleNotification}>
        {mutationFn => (
          <Alert
            open={open}
            onConfirm={() => handleDelete(mutationFn)}
            onCancel={() => setOpen(false)}
            message={t('admin:deleteContentDomainMessage')}
            confirm={t('common:delete')}
          />
        )}
      </AdminUpdateClientComponent>
    );
  }

  function renderButton() {
    return (
      <Button
        color="inherit"
        variant="text"
        onClick={() => setOpen(true)}
        endIcon={<CustomSVG name="delete" />}>
        <Typography>{t('common:delete')}</Typography>
      </Button>
    );
  }

  return (
    <Fragment>
      {renderButton()}
      {renderConfirmation()}
    </Fragment>
  );
};

export default DeleteContentDomain;
